import { durationMs } from "odyssey-shared";
import { lengthWords, waitWords } from "../server-status.ts";
import { countdownClock } from "./countdown-clock.ts";
import { say } from "../../words.ts";

/**
 * The warning in the world before maintenance (ADR-078 decision 6; the design's `MaintWarning`, a visual
 * reference). The gateway sends `maintenance` with how long until the start and the planned end; the page fixes both
 * at receipt on its own clock and times the rest: a toast at 30, 15 and 10 minutes before the start, or the one due
 * when the notice arrives inside them, each once; and from 5 minutes the strip at top centre until the server closes
 * the page with 4011, counting down the last minute beside Log out now. A cancel clears both. Every word is in
 * the language file (`hud.maintenance`) and is a draft for the author.
 */

/**
 * The `maintenance` message's fields.
 */
export interface MaintenanceNotice {
  readonly scheduled: boolean;
  readonly startsInMs: number;
  readonly endsInMs: number;
}

/**
 * The start and the planned end on the page's clock, the end null when none was given.
 */
export interface MaintenanceCountdown {
  readonly startsAt: number;
  readonly endsAt: number | null;
}

/**
 * What a toast or the strip says.
 */
export interface WarningWords {
  readonly title: string;
  readonly text: string;
}

/**
 * The strip's words, and whether it offers Log out now, which it does in the last minute.
 */
export interface StripWords extends WarningWords {
  readonly logOut: boolean;
}

/**
 * The minutes before the start a toast warns at.
 */
export const TOAST_MINUTES = [30, 15, 10] as const;

/**
 * The minutes before the start the strip shows from.
 */
export const STRIP_MINUTES = 5;

const MINUTE = 60_000;

/**
 * How long a toast stays: the motion table's hold.
 */
const TOAST_HOLD_MS = durationMs("toast-hold");

/**
 * How long past the start the strip keeps counting while the close has not yet arrived.
 */
const AFTER_START_MS = MINUTE;

/**
 * The countdown a notice gives at a time, or null for a cancel.
 *
 * @param notice The notice.
 * @param now The page's clock when it arrived.
 * @returns The countdown, or null.
 */
export function countdownOf(notice: MaintenanceNotice, now: number): MaintenanceCountdown | null {
  if (!notice.scheduled) {
    return null;
  }
  return { startsAt: now + notice.startsInMs, endsAt: notice.endsInMs < 0 ? null : now + notice.endsInMs };
}

/**
 * The toast due now: the smallest of `TOAST_MINUTES` the start is within and not yet shown, and none once the strip
 * shows.
 *
 * @param countdown The countdown.
 * @param now The page's clock.
 * @param shown The toasts already shown, by their minutes.
 * @returns The toast's minutes, or null.
 */
export function dueToast(countdown: MaintenanceCountdown, now: number, shown: readonly number[]): number | null {
  const left = countdown.startsAt - now;
  if (left <= STRIP_MINUTES * MINUTE) {
    return null;
  }
  const due = TOAST_MINUTES.filter((minutes) => left <= minutes * MINUTE);
  const next = due.at(-1);
  return next === undefined || shown.includes(next) ? null : next;
}

/**
 * A toast's words: the start's time, how soon, and the planned length when one was given.
 *
 * @param countdown The countdown.
 * @param now The page's clock.
 * @param clock The time of day of a page time.
 * @returns The words.
 */
export function toastOf(countdown: MaintenanceCountdown, now: number, clock: (ms: number) => string): WarningWords {
  const soon = waitWords(countdown.startsAt - now);
  return { title: say("hud.maintenance.toastTitle", { time: clock(countdown.startsAt) }), text: countdown.endsAt === null ? say("hud.maintenance.toastIn", { wait: soon }) : say("hud.maintenance.toastInFor", { wait: soon, length: lengthWords(countdown.endsAt - countdown.startsAt) }) };
}

/**
 * The strip's words now, or null before `STRIP_MINUTES`.
 *
 * @param countdown The countdown.
 * @param now The page's clock.
 * @param clock The time of day of a page time.
 * @returns The words, or null.
 */
export function stripOf(countdown: MaintenanceCountdown, now: number, clock: (ms: number) => string): StripWords | null {
  const left = countdown.startsAt - now;
  if (left > STRIP_MINUTES * MINUTE) {
    return null;
  }
  const at = clock(countdown.startsAt);
  if (left > MINUTE) {
    return { title: say("hud.maintenance.stripTitle", { wait: waitWords(left) }), text: say("hud.maintenance.stripText", { time: at }), logOut: false };
  }
  const seconds = Math.max(0, Math.ceil(left / 1000));
  return { title: say("hud.maintenance.lastTitle", { clock: countdownClock(seconds) }), text: say("hud.maintenance.lastText", { time: at }), logOut: true };
}

/**
 * What shows now: the toast, and the strip.
 */
export interface WarningSnapshot {
  readonly toast: WarningWords | null;
  readonly strip: StripWords | null;
}

/**
 * What the warnings work through: the page's clock, the time of day of a page time, a timer that returns its cancel,
 * and the sound each toast plays as it appears.
 */
export interface MaintenanceWarningsDeps {
  readonly now: () => number;
  readonly clock: (ms: number) => string;
  readonly schedule?: (fn: () => void, ms: number) => () => void;
  readonly sound?: () => void;
}

/**
 * Schedules on the page's timer.
 *
 * @param fn What to run.
 * @param ms How long to wait.
 * @returns Its cancel.
 */
function pageTimer(fn: () => void, ms: number): () => void {
  const timer = setTimeout(fn, ms);
  return () => clearTimeout(timer);
}

/**
 * Whether two sets of words say the same.
 *
 * @param a One, or null.
 * @param b The other, or null.
 * @returns True when both are none or say the same.
 */
function same(a: StripWords | WarningWords | null, b: StripWords | WarningWords | null): boolean {
  return a === null || b === null ? a === b : a.title === b.title && a.text === b.text;
}

/**
 * The warnings a page holds, which the HUD reads through `subscribe` and `getSnapshot`: moved by each `maintenance`
 * notice and by its own timers, one to the next toast or the strip's start, then one a second while the strip counts.
 */
export class MaintenanceWarnings {
  private countdown: MaintenanceCountdown | null = null;
  private shown: number[] = [];
  private snapshot: WarningSnapshot = { toast: null, strip: null };
  private readonly listeners = new Set<() => void>();
  private cancelTick: (() => void) | null = null;
  private cancelToast: (() => void) | null = null;
  private readonly schedule: (fn: () => void, ms: number) => () => void;

  /**
   * @param deps The clock, the time of day and the timer.
   */
  public constructor(private readonly deps: MaintenanceWarningsDeps) {
    this.schedule = deps.schedule ?? pageTimer;
  }

  /**
   * Takes a `maintenance` notice: a schedule restarts the warnings from it, a cancel clears them.
   *
   * @param notice The notice.
   */
  public receive(notice: MaintenanceNotice): void {
    this.cancelTick?.();
    this.cancelTick = null;
    this.cancelToast?.();
    this.cancelToast = null;
    this.countdown = countdownOf(notice, this.deps.now());
    this.shown = [];
    if (this.countdown === null) {
      this.set({ toast: null, strip: null });
      return;
    }
    this.snapshot = { toast: null, strip: this.snapshot.strip };
    this.evaluate();
  }

  /**
   * Adds a listener for changed warnings.
   *
   * @param listener Called after each change.
   * @returns A function that removes it.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /**
   * What shows now.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): WarningSnapshot => this.snapshot;

  /**
   * Works out what shows now, shows a toast that is due, and waits for the next change.
   */
  private evaluate(): void {
    const countdown = this.countdown;
    if (countdown === null) {
      return;
    }
    const now = this.deps.now();
    let toast = this.snapshot.toast;
    const due = dueToast(countdown, now, this.shown);
    if (due !== null) {
      this.shown = TOAST_MINUTES.filter((minutes) => minutes >= due);
      toast = toastOf(countdown, now, this.deps.clock);
      this.deps.sound?.();
      this.cancelToast?.();
      this.cancelToast = this.schedule(() => {
        this.cancelToast = null;
        this.set({ ...this.snapshot, toast: null });
      }, TOAST_HOLD_MS);
    }
    const strip = stripOf(countdown, now, this.deps.clock);
    if (!same(toast, this.snapshot.toast) || !same(strip, this.snapshot.strip) || strip?.logOut !== this.snapshot.strip?.logOut) {
      this.set({ toast, strip });
    }
    const left = countdown.startsAt - now;
    if (left < -AFTER_START_MS) {
      return;
    }
    const next = left > STRIP_MINUTES * MINUTE ? Math.min(...[...TOAST_MINUTES, STRIP_MINUTES].map((minutes) => left - minutes * MINUTE).filter((wait) => wait > 0)) : 1_000;
    this.cancelTick = this.schedule(() => {
      this.cancelTick = null;
      this.evaluate();
    }, next);
  }

  /**
   * Replaces the snapshot and tells the listeners.
   *
   * @param snapshot The new snapshot.
   */
  private set(snapshot: WarningSnapshot): void {
    this.snapshot = snapshot;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
